import { Box3, CatmullRomCurve3, Vector3 } from 'three'

interface GuideShape {
  tilt: number
  yAmplitude: number
  yOffset: number
  yPhase: number
  zAmplitude: number
  zPhase: number
}

const createGuidePoints = ({
  tilt,
  yAmplitude,
  yOffset,
  yPhase,
  zAmplitude,
  zPhase,
}: GuideShape): ReadonlyArray<readonly [number, number, number]> =>
  Array.from({ length: 13 }, (_, index) => {
    const progress = index / 12
    const centered = progress - 0.5
    const x = centered * 10
    const y =
      yOffset +
      Math.sin((progress + yPhase) * Math.PI * 2) * yAmplitude +
      centered * tilt
    const z =
      Math.sin((progress + zPhase) * Math.PI * 2) *
      zAmplitude *
      Math.sin(progress * Math.PI)

    return [x, y, z] as const
  })

const KEYFRAME_POINTS = [
  createGuidePoints({
    tilt: -0.25,
    yAmplitude: 0.58,
    yOffset: 0.55,
    yPhase: 0.06,
    zAmplitude: 0.58,
    zPhase: 0.2,
  }),
  createGuidePoints({
    tilt: 0.3,
    yAmplitude: 1.05,
    yOffset: 0.05,
    yPhase: -0.12,
    zAmplitude: 0.68,
    zPhase: 0.43,
  }),
  createGuidePoints({
    tilt: -0.2,
    yAmplitude: 1.22,
    yOffset: 0,
    yPhase: 0.06,
    zAmplitude: 0.72,
    zPhase: 0.67,
  }),
  createGuidePoints({
    tilt: 0.42,
    yAmplitude: 1.02,
    yOffset: -0.05,
    yPhase: 0.28,
    zAmplitude: 0.66,
    zPhase: 0.9,
  }),
  createGuidePoints({
    tilt: -0.45,
    yAmplitude: 0.72,
    yOffset: -0.3,
    yPhase: 0.48,
    zAmplitude: 0.52,
    zPhase: 1.1,
  }),
]

const smoothstep = (value: number): number => value * value * (3 - 2 * value)

const createEqualLengthSamples = (
  points: ReadonlyArray<readonly [number, number, number]>,
  jointCount: number,
  targetLength: number,
): Float32Array => {
  const vectors = points.map(([x, y, z]) => new Vector3(x, y, z))
  const bounds = new Box3().setFromPoints(vectors)
  const center = bounds.getCenter(new Vector3())
  const firstCurve = new CatmullRomCurve3(
    vectors,
    false,
    'centripetal',
    0.5,
  )
  const scale = targetLength / firstCurve.getLength()
  const scaledPoints = vectors.map((point) =>
    point.clone().sub(center).multiplyScalar(scale).add(center),
  )
  const curve = new CatmullRomCurve3(
    scaledPoints,
    false,
    'centripetal',
    0.5,
  )
  const samples = curve.getSpacedPoints(jointCount - 1)
  const output = new Float32Array(jointCount * 3)

  for (let index = 0; index < jointCount; index += 1) {
    const sample = samples[index]
    const offset = index * 3
    output[offset] = sample.x
    output[offset + 1] = sample.y
    output[offset + 2] = sample.z
  }

  return output
}

export class RibbonGuide {
  private readonly keyframes: Float32Array[]

  constructor(
    private readonly jointCount: number,
    targetLength: number,
  ) {
    this.keyframes = KEYFRAME_POINTS.map((points) =>
      createEqualLengthSamples(points, jointCount, targetLength),
    )
  }

  write(progress: number, output: Float32Array): void {
    const scaledProgress =
      Math.min(1, Math.max(0, progress)) * (this.keyframes.length - 1)
    const fromIndex = Math.floor(scaledProgress)
    const toIndex = Math.min(this.keyframes.length - 1, fromIndex + 1)
    const mixAmount = smoothstep(scaledProgress - fromIndex)
    const from = this.keyframes[fromIndex]
    const to = this.keyframes[toIndex]

    for (let index = 0; index < this.jointCount * 3; index += 1) {
      output[index] = from[index] + (to[index] - from[index]) * mixAmount
    }
  }
}
